import { BaseOrder, Status } from '../../models/orders.model';
import {
  patchState,
  signalStore,
  withMethods,
  withState,
  getState,
  withHooks,
} from '@ngrx/signals';
import { effect, inject } from '@angular/core';
import { DataFetchService } from '../services/data-fetch/data-fetch.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { of, pipe, switchMap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { withPagination } from './features/pagination.feature';
import { withColumnOrdering } from './features/column-ordering.feature';
import { withSorting } from './features/sorting.feature';
import { withViewMode } from './features/view-mode.feature';
import { Router } from '@angular/router';

type ScreenState = {
  selectedOrder: BaseOrder | null;
};

const initialState: ScreenState = {
  selectedOrder: null,
};

export const DetailsStore = signalStore(
  withState(initialState),
  withPagination(),
  withColumnOrdering(),
  withSorting(),
  withViewMode(),
  withMethods((store) => ({
    updateLocalOrderStatus(orderId: string, newStatus: Status) {
      const state = getState(store);
      const currentSelectedOrder = state.selectedOrder;
      if (currentSelectedOrder && currentSelectedOrder.orderId === orderId) {
        const updatedOrder = {
          ...currentSelectedOrder,
          orderStatus: newStatus,
        };
        patchState(store, {
          selectedOrder: updatedOrder,
        });
      }
    },
  })),
  withMethods((store, fetchService = inject(DataFetchService)) => ({
    setSelectedOrder: rxMethod<string>(
      pipe(
        switchMap((orderId) => {
          return fetchService.getSingleOrder(orderId).pipe(
            tapResponse({
              next: (selectedOrder) => {
                patchState(store, { selectedOrder });
                if (
                  selectedOrder &&
                  selectedOrder.positions &&
                  !getState(store).displayedColumns.length
                ) {
                  store.updateDisplayedColumns(
                    Object.keys(selectedOrder.positions[0])
                  );
                }
              },
              error: (error) => console.error(error),
            })
          );
        })
      )
    ),
    updateOrderStatus: rxMethod<{ orderId: string; newStatus: Status }>(
      pipe(
        switchMap(({ orderId, newStatus }) =>
          fetchService.updateOrderStatus(orderId, newStatus).pipe(
            tapResponse({
              next: () => {
                store.updateLocalOrderStatus(orderId, newStatus);
              },
              error: (error) => console.error(error),
            })
          )
        )
      )
    ),
    //** TO BE TESTED */
    updatePositionStatus: rxMethod<{ partId: string; newStatus: Status }>(
      pipe(
        switchMap(({ partId, newStatus }) =>
          fetchService.updateOrderStatus(partId, newStatus).pipe(
            tapResponse({
              next: () => {
                const state = getState(store);
                if (state.selectedOrder && state.selectedOrder.positions) {
                  const updatedParts = state.selectedOrder?.positions.map(
                    (part) =>
                      part.itemId === partId
                        ? { ...part, itemStatus: newStatus }
                        : part
                  );
                  patchState(store, {
                    selectedOrder: {
                      ...state.selectedOrder,
                      positions: updatedParts,
                    },
                  });
                }
              },
              error: (error) => console.error(error),
            })
          )
        )
      )
    ),
    updateSelectedOrder: rxMethod<BaseOrder>(
      pipe(
        switchMap((order: BaseOrder) => {
          patchState(store, { selectedOrder: order });
          return of(order);
        })
      )
    )
  })),
  withHooks({
    onInit(store) {
      const router = inject(Router);

      effect(() => {
        const state = getState(store);
        const { pageIndex } = state;
        //const selectedViewMode = state.selectedViewMode;
        const { sorting } = state;

        const urlTree = router.createUrlTree([], {
          queryParams: {
            page: pageIndex,
            //   selectedViewMode: selectedViewMode,
            sortCol: sorting.direction ? sorting.active : null,
            sortDir: sorting.direction ? sorting.direction : null,
          },
        });
        window.history.replaceState(null, '', router.serializeUrl(urlTree));
      });

      const urlParams = new URLSearchParams(
        router.routerState.snapshot.url.split('?')[1]
      );

      const pageIndex = urlParams.get('page');
      if (pageIndex) {
        patchState(store, { pageIndex: parseInt(pageIndex, 10) });
      }

      // const selectedViewMode = urlParams.get('selectedViewMode');
      // if (selectedViewMode) {
      //   patchState(store, { selectedViewMode: selectedViewMode as 'list' | 'cards'});
      // }

      const sortCol = urlParams.get('sortCol');
      const sortDir = urlParams.get('sortDir');
      if (sortDir && sortCol) {
        patchState(store, {
          sorting: {
            active: sortCol,
            direction: sortDir as 'asc' | 'desc' | '',
          },
        });
      }
    },
  })
);
